import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  listUnlinkedMocoCompanies,
  linkMocoCompanyToClient,
  createClientFromMocoCompany,
  getMocoSyncState,
  listLinkedMocoClients,
  unlinkMocoCompanyFromClient,
  listIgnoredMocoCompanies,
  ignoreMocoCompany,
  unignoreMocoCompany,
  type MocoUnlinkedCompany,
  type MocoLinkedClient,
  type MocoIgnoredCompany,
} from "@/lib/moco.functions";
import { listClients } from "@/lib/customers.functions";
import { listStaffProfiles } from "@/lib/staff.functions";
import { useAdminSession } from "@/hooks/use-admin-session";
import {
  ArrowRightLeft,
  Loader2,
  RefreshCcw,
  Link as LinkIcon,
  Plus,
  X,
  ChevronDown,
  Unlink,
  EyeOff,
  RotateCcw,
} from "lucide-react";

export const Route = createFileRoute("/_admin/admin/moco-sync")({
  // Ticket AD4a: Sprungziel aus dem Arbeitsvorrat (?tab=unlinked).
  validateSearch: (search: Record<string, unknown>): { tab?: "unlinked" | "linked" } => ({
    tab: search.tab === "linked" ? ("linked" as const) : search.tab === "unlinked" ? ("unlinked" as const) : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Moco-Abgleich – Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: MocoSyncPage,
});

function formatDate(iso: string | null | undefined) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-DE", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function MocoSyncPage() {
  const session = useAdminSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const listFn = useServerFn(listUnlinkedMocoCompanies);
  const stateFn = useServerFn(getMocoSyncState);
  const listClientsFn = useServerFn(listClients);
  const listStaffFn = useServerFn(listStaffProfiles);
  const linkFn = useServerFn(linkMocoCompanyToClient);
  const createFn = useServerFn(createClientFromMocoCompany);
  const linkedListFn = useServerFn(listLinkedMocoClients);
  const unlinkFn = useServerFn(unlinkMocoCompanyFromClient);
  const ignoredListFn = useServerFn(listIgnoredMocoCompanies);
  const ignoreFn = useServerFn(ignoreMocoCompany);
  const unignoreFn = useServerFn(unignoreMocoCompany);

  const [tab, setTab] = useState<"unlinked" | "linked">(
    Route.useSearch().tab ?? "unlinked",
  );
  const [archivedOpen, setArchivedOpen] = useState(false);
  const [ignoredOpen, setIgnoredOpen] = useState(false);
  const [ignoreTarget, setIgnoreTarget] = useState<MocoUnlinkedCompany | null>(
    null,
  );

  const ignoredQ = useQuery({
    queryKey: ["admin", "moco", "ignored"],
    queryFn: () => ignoredListFn(),
    enabled: session.isAdmin,
  });

  const linkedQ = useQuery({
    queryKey: ["admin", "moco", "linked"],
    queryFn: () => linkedListFn(),
    enabled: session.isAdmin && tab === "linked",
  });

  const unlinkedQ = useQuery({
    queryKey: ["admin", "moco", "unlinked"],
    queryFn: () => listFn({ data: {} }),
    enabled: session.isAdmin,
  });
  const stateQ = useQuery({
    queryKey: ["admin", "moco", "state"],
    queryFn: () => stateFn(),
    enabled: session.isAdmin,
  });
  const clientsQ = useQuery({
    queryKey: ["admin", "clients"],
    queryFn: () => listClientsFn(),
    enabled: session.isAdmin,
  });
  const staffQ = useQuery({
    queryKey: ["admin", "staff-profiles"],
    queryFn: () => listStaffFn(),
    enabled: session.isAdmin,
  });

  const refreshMutation = useMutation({
    mutationFn: () => listFn({ data: { refresh: true } }),
    onSuccess: () => {
      toast.success("Moco-Kunden neu geladen.");
      queryClient.invalidateQueries({ queryKey: ["admin", "moco"] });
    },
    onError: (err: Error) =>
      toast.error(err.message || "Moco-Abruf fehlgeschlagen."),
  });

  const [linkTarget, setLinkTarget] = useState<MocoUnlinkedCompany | null>(null);
  const [createTarget, setCreateTarget] = useState<MocoUnlinkedCompany | null>(null);

  if (session.status === "authenticated" && !session.isAdmin) {
    return (
      <div className="max-w-2xl">
        <h1 className="font-display text-3xl font-extrabold md:text-4xl">Moco-Abgleich</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Nur Administratoren dürfen den Moco-Abgleich durchführen.
        </p>
      </div>
    );
  }

  const rows = unlinkedQ.data ?? [];
  const activeRows = rows.filter((r) => r.has_active_projects);
  const archivedRows = rows.filter((r) => !r.has_active_projects);
  const state = stateQ.data;

  return (
    <div className="mx-auto max-w-6xl">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <ArrowRightLeft className="h-4 w-4" /> Moco-Abgleich
          </div>
          <h1 className="mt-1 font-display text-3xl font-extrabold md:text-4xl">
            Moco-Kunden abgleichen
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Moco-Kunden ohne Verknüpfung zu einem creategy-Kunden. Verknüpfen Sie bestehende Kunden
            oder legen Sie neue Kunden mit vorausgefüllten Moco-Stammdaten an.
          </p>
        </div>
        <button
          onClick={() => refreshMutation.mutate()}
          disabled={refreshMutation.isPending}
          className="inline-flex items-center gap-2 rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground disabled:opacity-60"
        >
          {refreshMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCcw className="h-4 w-4" />
          )}
          Jetzt aus Moco laden
        </button>
      </div>

      <div className="mt-6 rounded-md border border-border bg-muted/30 px-4 py-3 text-sm">
        <div className="flex flex-wrap gap-x-6 gap-y-1">
          <span>
            <span className="text-muted-foreground">Letzter Erfolgslauf: </span>
            <strong>{formatDate(state?.last_success_at)}</strong>
          </span>
          <span>
            <span className="text-muted-foreground">Unverknüpft mit aktiven Projekten: </span>
            <strong>{activeRows.length}</strong>
          </span>
          <span>
            <span className="text-muted-foreground">Archiviert: </span>
            <strong>{archivedRows.length}</strong>
          </span>
          {state?.last_error && (
            <span className="text-destructive">
              Letzter Fehler ({formatDate(state.last_error_at)}): {state.last_error}
            </span>
          )}
        </div>
      </div>

      <div className="mt-6 flex gap-2 border-b border-border">
        {(
          [
            ["unlinked", `Abgleich (${activeRows.length})`],
            ["linked", "Verknüpfte Kunden"],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`-mb-px border-b-2 px-4 py-2 font-display text-sm font-extrabold transition-colors ${
              tab === key
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "unlinked" && (
        <>
          <div className="mt-6 overflow-hidden rounded-md border border-border">
            <UnlinkedTable
              rows={activeRows}
              loading={unlinkedQ.isLoading}
              emptyLabel='Keine unverknüpften Moco-Kunden mit aktiven Projekten. Klicken Sie auf „Jetzt aus Moco laden", um die Liste zu aktualisieren.'
              onLink={setLinkTarget}
              onCreate={setCreateTarget}
              onIgnore={setIgnoreTarget}
            />
          </div>

          {archivedRows.length > 0 && (
            <div className="mt-6 overflow-hidden rounded-md border border-border">
              <button
                onClick={() => setArchivedOpen((v) => !v)}
                className="flex w-full items-center justify-between gap-3 bg-muted/40 px-4 py-3 text-left"
                aria-expanded={archivedOpen}
              >
                <span className="font-display text-sm font-extrabold">
                  Archivierte Moco-Kunden ({archivedRows.length})
                </span>
                <ChevronDown
                  className={`h-4 w-4 shrink-0 transition-transform ${archivedOpen ? "rotate-180" : ""}`}
                />
              </button>
              {archivedOpen && (
                <UnlinkedTable
                  rows={archivedRows}
                  loading={false}
                  emptyLabel="Keine archivierten Moco-Kunden."
                  onLink={setLinkTarget}
                  onCreate={setCreateTarget}
                  onIgnore={setIgnoreTarget}
                />
              )}
            </div>
          )}

          <IgnoredSection
            rows={ignoredQ.data ?? []}
            loading={ignoredQ.isLoading}
            open={ignoredOpen}
            onToggle={() => setIgnoredOpen((v) => !v)}
            onRestore={async (r) => {
              try {
                await unignoreFn({ data: { moco_company_id: r.moco_company_id } });
                toast.success(
                  `„${r.name}" wurde wieder in den Abgleich aufgenommen.`,
                );
                queryClient.invalidateQueries({ queryKey: ["admin", "moco"] });
              } catch (err) {
                toast.error(
                  err instanceof Error ? err.message : "Aktion fehlgeschlagen.",
                );
              }
            }}
          />
        </>
      )}

      {ignoreTarget && (
        <IgnoreDialog
          target={ignoreTarget}
          onClose={() => setIgnoreTarget(null)}
          onConfirm={async (reason) => {
            try {
              await ignoreFn({
                data: {
                  moco_company_id: ignoreTarget.moco_company_id,
                  reason: reason || undefined,
                },
              });
              toast.success(`„${ignoreTarget.name}" wird künftig ignoriert.`);
              setIgnoreTarget(null);
              setIgnoredOpen(true);
              queryClient.invalidateQueries({ queryKey: ["admin", "moco"] });
            } catch (err) {
              toast.error(
                err instanceof Error ? err.message : "Ignorieren fehlgeschlagen.",
              );
            }
          }}
        />
      )}


      {tab === "linked" && (
        <LinkedTab
          rows={linkedQ.data ?? []}
          loading={linkedQ.isLoading}
          onUnlink={async (clientId) => {
            try {
              const res: any = await unlinkFn({ data: { client_id: clientId } });
              const n = res?.hidden_count ?? 0;
              toast.success(
                n > 0
                  ? `Verknüpfung gelöst. ${n} Rechnung${n === 1 ? "" : "en"} im Cockpit ausgeblendet.`
                  : "Verknüpfung gelöst. Keine sichtbaren Moco-Rechnungen vorhanden.",
              );
              queryClient.invalidateQueries({ queryKey: ["admin", "moco"] });
              queryClient.invalidateQueries({ queryKey: ["admin", "clients"] });
            } catch (err) {
              toast.error(err instanceof Error ? err.message : "Lösen fehlgeschlagen.");
            }
          }}
        />
      )}


      {linkTarget && (
        <LinkDialog
          target={linkTarget}
          clients={clientsQ.data ?? []}
          onClose={() => setLinkTarget(null)}
          onConfirm={async (clientId) => {
            try {
              await linkFn({
                data: { moco_company_id: linkTarget.moco_company_id, client_id: clientId },
              });
              toast.success("Kunde verknüpft.");
              setLinkTarget(null);
              queryClient.invalidateQueries({ queryKey: ["admin", "moco"] });
              queryClient.invalidateQueries({ queryKey: ["admin", "clients"] });
            } catch (err) {
              toast.error(err instanceof Error ? err.message : "Verknüpfen fehlgeschlagen.");
            }
          }}
        />
      )}

      {createTarget && (
        <CreateDialog
          target={createTarget}
          staff={(staffQ.data?.staff ?? []).map((s) => ({
            id: s.id,
            name:
              [s.firstName, s.lastName].filter(Boolean).join(" ") ||
              s.fullName ||
              s.email ||
              "—",
          }))}
          onClose={() => setCreateTarget(null)}
          onConfirm={async (managerId) => {
            try {
              const created = await createFn({
                data: {
                  moco_company_id: createTarget.moco_company_id,
                  account_manager_id: managerId,
                },
              });
              toast.success("Kunde angelegt.");
              setCreateTarget(null);
              queryClient.invalidateQueries({ queryKey: ["admin", "moco"] });
              queryClient.invalidateQueries({ queryKey: ["admin", "clients"] });
              navigate({ to: "/admin/clients/$id", params: { id: created.id } });
            } catch (err) {
              toast.error(err instanceof Error ? err.message : "Anlegen fehlgeschlagen.");
            }
          }}
        />
      )}
    </div>
  );
}

function LinkDialog({
  target,
  clients,
  onClose,
  onConfirm,
}: {
  target: MocoUnlinkedCompany;
  clients: { id: string; organization_name: string; moco_client_number: string | null }[];
  onClose: () => void;
  onConfirm: (clientId: string) => Promise<void>;
}) {
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return clients.slice(0, 50);
    return clients
      .filter(
        (c) =>
          c.organization_name.toLowerCase().includes(term) ||
          (c.moco_client_number ?? "").toLowerCase().includes(term),
      )
      .slice(0, 50);
  }, [clients, q]);

  return (
    <DialogShell title={`Moco-Kunde verknüpfen: ${target.name}`} onClose={onClose}>
      <p className="text-sm text-muted-foreground">
        Wählen Sie den bestehenden creategy-Kunden, dem die Moco-Nummer{" "}
        <strong>{target.moco_company_id}</strong> zugeordnet werden soll.
      </p>
      <input
        autoFocus
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Kunden suchen …"
        className="mt-4 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
      />
      <div className="mt-3 max-h-64 overflow-y-auto rounded-md border border-border">
        {filtered.length === 0 && (
          <p className="px-3 py-4 text-sm text-muted-foreground">Kein Treffer.</p>
        )}
        {filtered.map((c) => (
          <label
            key={c.id}
            className="flex cursor-pointer items-center gap-2 border-b border-border px-3 py-2 text-sm last:border-none hover:bg-secondary"
          >
            <input
              type="radio"
              checked={selected === c.id}
              onChange={() => setSelected(c.id)}
            />
            <span className="flex-1">{c.organization_name}</span>
            <span className="text-xs text-muted-foreground">
              {c.moco_client_number ? `Moco-Nr. ${c.moco_client_number}` : "ohne Kundennummer"}
            </span>
          </label>
        ))}
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button
          onClick={onClose}
          className="rounded-md border border-border px-3 py-1.5 text-sm"
        >
          Abbrechen
        </button>
        <button
          disabled={!selected || busy}
          onClick={async () => {
            setBusy(true);
            try {
              await onConfirm(selected);
            } finally {
              setBusy(false);
            }
          }}
          className="inline-flex items-center gap-2 rounded-md bg-brand-button px-4 py-1.5 text-sm font-semibold text-brand-button-foreground disabled:opacity-60"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Verknüpfen
        </button>
      </div>
    </DialogShell>
  );
}

function CreateDialog({
  target,
  staff,
  onClose,
  onConfirm,
}: {
  target: MocoUnlinkedCompany;
  staff: { id: string; name: string }[];
  onClose: () => void;
  onConfirm: (managerId: string) => Promise<void>;
}) {
  const [managerId, setManagerId] = useState("");
  const [busy, setBusy] = useState(false);

  return (
    <DialogShell title="Neuen creategy-Kunden anlegen" onClose={onClose}>
      <dl className="mt-2 grid grid-cols-3 gap-2 text-sm">
        <dt className="text-muted-foreground">Firmenname</dt>
        <dd className="col-span-2 font-medium">{target.name}</dd>
        <dt className="text-muted-foreground">Moco-Kundennummer</dt>
        <dd className="col-span-2 font-mono">{target.moco_company_id}</dd>
        {target.identifier && (
          <>
            <dt className="text-muted-foreground">Identifier</dt>
            <dd className="col-span-2">{target.identifier}</dd>
          </>
        )}
      </dl>
      <label className="mt-5 block text-sm font-medium">
        Ansprechpartner (Pflicht)
        <select
          value={managerId}
          onChange={(e) => setManagerId(e.target.value)}
          className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        >
          <option value="">— bitte wählen —</option>
          {staff.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </label>
      <div className="mt-5 flex justify-end gap-2">
        <button
          onClick={onClose}
          className="rounded-md border border-border px-3 py-1.5 text-sm"
        >
          Abbrechen
        </button>
        <button
          disabled={!managerId || busy}
          onClick={async () => {
            setBusy(true);
            try {
              await onConfirm(managerId);
            } finally {
              setBusy(false);
            }
          }}
          className="inline-flex items-center gap-2 rounded-md bg-brand-button px-4 py-1.5 text-sm font-semibold text-brand-button-foreground disabled:opacity-60"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Anlegen
        </button>
      </div>
    </DialogShell>
  );
}

function DialogShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-xl rounded-lg border border-border bg-background p-6 shadow-xl">
        <div className="flex items-start justify-between">
          <h2 className="font-display text-xl font-extrabold">{title}</h2>
          <button
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-secondary"
            aria-label="Schließen"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-3">{children}</div>
      </div>
    </div>
  );
}

function UnlinkedTable({
  rows,
  loading,
  emptyLabel,
  onLink,
  onCreate,
  onIgnore,
}: {
  rows: MocoUnlinkedCompany[];
  loading: boolean;
  emptyLabel: string;
  onLink: (r: MocoUnlinkedCompany) => void;
  onCreate: (r: MocoUnlinkedCompany) => void;
  onIgnore: (r: MocoUnlinkedCompany) => void;
}) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
        <tr>
          <th className="px-4 py-2 font-semibold">Kunde</th>
          <th className="px-4 py-2 font-semibold">Projekte</th>
          <th className="px-4 py-2 text-right font-semibold">Aktion</th>
        </tr>
      </thead>
      <tbody>
        {loading && (
          <tr>
            <td colSpan={3} className="px-4 py-6 text-center text-muted-foreground">
              <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Lädt …
            </td>
          </tr>
        )}
        {!loading && rows.length === 0 && (
          <tr>
            <td colSpan={3} className="px-4 py-6 text-center text-muted-foreground">
              {emptyLabel}
            </td>
          </tr>
        )}
        {rows.map((r) => (
          <tr key={r.moco_company_id} className="border-t border-border">
            <td className="px-4 py-3">
              <span className="font-display text-base font-extrabold">{r.name}</span>
              <span className="ml-2 font-mono text-xs text-muted-foreground">
                Moco-Nr. {r.moco_company_id}
                {r.identifier ? ` · ${r.identifier}` : ""}
              </span>
            </td>
            <td className="px-4 py-3 text-xs text-muted-foreground">
              {r.active_projects_count} aktiv / {r.projects_count} gesamt
            </td>
            <td className="px-4 py-3 text-right">
              <div className="inline-flex flex-wrap justify-end gap-2">
                <button
                  onClick={() => onLink(r)}
                  className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-xs font-semibold hover:bg-secondary"
                >
                  <LinkIcon className="h-3.5 w-3.5" /> Verknüpfen
                </button>
                <button
                  onClick={() => onCreate(r)}
                  className="inline-flex items-center gap-1 rounded-md bg-brand-button px-2.5 py-1 text-xs font-semibold text-brand-button-foreground"
                >
                  <Plus className="h-3.5 w-3.5" /> Neu anlegen
                </button>
                <button
                  onClick={() => onIgnore(r)}
                  className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-xs font-semibold text-muted-foreground hover:bg-secondary"
                >
                  <EyeOff className="h-3.5 w-3.5" /> Ignorieren
                </button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function IgnoreDialog({
  target,
  onClose,
  onConfirm,
}: {
  target: MocoUnlinkedCompany;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4">
      <div className="w-full max-w-lg rounded-lg border border-border bg-background p-6 shadow-lg">
        <div className="flex items-start justify-between gap-4">
          <h2 className="font-display text-xl font-extrabold">
            Moco-Kunde ignorieren
          </h2>
          <button onClick={onClose} aria-label="Schließen">
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="mt-3 text-sm text-muted-foreground">
          „{target.name}" (Moco-Nr. {target.moco_company_id}) wird aus dem
          Abgleich entfernt und erscheint künftig in der Sektion „Ohne
          Cockpit-Zugang". Der Kunde erhält keinen Zugang zum Kundencockpit und
          löst keine Benachrichtigungen mehr aus.
        </p>
        <label className="mt-4 block text-sm font-semibold">
          Grund (optional)
        </label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          maxLength={500}
          placeholder="z. B. reiner Beratungskunde, kein Portalbedarf"
          className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        />
        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-md border border-border px-3 py-1.5 text-sm font-semibold"
          >
            Abbrechen
          </button>
          <button
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await onConfirm(reason.trim());
              } finally {
                setBusy(false);
              }
            }}
            className="inline-flex items-center gap-2 rounded-md bg-brand-button px-3 py-1.5 text-sm font-semibold text-brand-button-foreground disabled:opacity-60"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Ignorieren
          </button>
        </div>
      </div>
    </div>
  );
}

function IgnoredSection({
  rows,
  loading,
  open,
  onToggle,
  onRestore,
}: {
  rows: MocoIgnoredCompany[];
  loading: boolean;
  open: boolean;
  onToggle: () => void;
  onRestore: (r: MocoIgnoredCompany) => Promise<void>;
}) {
  const [busyId, setBusyId] = useState<string | null>(null);

  return (
    <div className="mt-6 overflow-hidden rounded-md border border-border">
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-3 bg-muted/40 px-4 py-3 text-left"
        aria-expanded={open}
      >
        <span className="font-display text-sm font-extrabold">
          Ohne Cockpit-Zugang ({rows.length})
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <table className="w-full text-sm">
          <thead className="bg-muted/20 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-4 py-2 font-semibold">Kunde</th>
              <th className="px-4 py-2 font-semibold">Moco-Status</th>
              <th className="px-4 py-2 font-semibold">Ignoriert am</th>
              <th className="px-4 py-2 font-semibold">Mitarbeiter</th>
              <th className="px-4 py-2 font-semibold">Grund</th>
              <th className="px-4 py-2 text-right font-semibold">Aktion</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">
                  <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Lädt …
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">
                  Keine ignorierten Moco-Kunden.
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.moco_company_id} className="border-t border-border">
                <td className="px-4 py-3">
                  <span className="font-display text-base font-extrabold">{r.name}</span>
                  <span className="ml-2 font-mono text-xs text-muted-foreground">
                    Moco-Nr. {r.moco_company_id}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {r.projects_count === 0 && !r.stats_updated_at ? (
                    <span className="text-xs text-muted-foreground">—</span>
                  ) : r.has_active_projects ? (
                    <span className="inline-flex items-center rounded-full bg-brand-wahlen/15 px-2 py-0.5 text-xs font-semibold text-foreground">
                      Aktiv
                    </span>
                  ) : r.projects_count > 0 ? (
                    <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-semibold text-muted-foreground">
                      Archiviert
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">Keine Projekte</span>
                  )}
                  <div className="mt-1 text-[11px] text-muted-foreground">
                    {r.active_projects_count}/{r.projects_count} aktiv
                    {r.stats_updated_at ? ` · Stand ${formatDate(r.stats_updated_at)}` : ""}
                  </div>
                </td>
                <td className="px-4 py-3 text-xs text-muted-foreground">
                  {formatDate(r.ignored_at)}
                </td>
                <td className="px-4 py-3 text-xs text-muted-foreground">
                  {r.ignored_by_name ?? "—"}
                </td>
                <td className="px-4 py-3 text-xs text-muted-foreground">
                  {r.reason?.trim() ? r.reason : "—"}
                </td>

                <td className="px-4 py-3 text-right">
                  <button
                    disabled={busyId === r.moco_company_id}
                    onClick={async () => {
                      setBusyId(r.moco_company_id);
                      try {
                        await onRestore(r);
                      } finally {
                        setBusyId(null);
                      }
                    }}
                    className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-xs font-semibold hover:bg-secondary disabled:opacity-60"
                  >
                    {busyId === r.moco_company_id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <RotateCcw className="h-3.5 w-3.5" />
                    )}
                    Wieder in den Abgleich aufnehmen
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function LinkedTab({
  rows,
  loading,
  onUnlink,
}: {
  rows: MocoLinkedClient[];
  loading: boolean;
  onUnlink: (clientId: string) => Promise<void>;
}) {
  const [target, setTarget] = useState<MocoLinkedClient | null>(null);
  const [busy, setBusy] = useState(false);

  return (
    <>
      <div className="mt-6 overflow-hidden rounded-md border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-4 py-2 font-semibold">Website-Kunde</th>
              <th className="px-4 py-2 font-semibold">Moco-Kunde</th>
              <th className="px-4 py-2 text-right font-semibold">Aktion</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={3} className="px-4 py-6 text-center text-muted-foreground">
                  <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Lädt …
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={3} className="px-4 py-6 text-center text-muted-foreground">
                  Es bestehen noch keine Verknüpfungen.
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.client_id} className="border-t border-border">
                <td className="px-4 py-3">
                  <span className="font-display text-base font-extrabold">
                    {r.organization_name}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className="font-semibold">{r.moco_name ?? "—"}</span>
                  <span className="ml-2 font-mono text-xs text-muted-foreground">
                    Moco-Nr. {r.moco_client_number}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => setTarget(r)}
                    className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-xs font-semibold hover:bg-secondary"
                  >
                    <Unlink className="h-3.5 w-3.5" /> Verknüpfung lösen
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {target && (
        <DialogShell title="Verknüpfung lösen" onClose={() => setTarget(null)}>
          <p className="text-sm text-muted-foreground">
            Möchten Sie die Verknüpfung zwischen{" "}
            <strong className="text-foreground">{target.organization_name}</strong> und dem
            Moco-Kunden <strong className="text-foreground">{target.moco_client_number}</strong>{" "}
            wirklich lösen?
          </p>
          <p className="mt-3 text-sm text-muted-foreground">
            Alle aus Moco importierten Rechnungen dieses Kunden werden im Cockpit ausgeblendet.
            Manuell hochgeladene Rechnungen bleiben sichtbar. Die Rechnungen und PDFs bleiben
            intern erhalten.
          </p>
          <div className="mt-6 flex justify-end gap-2">
            <button
              onClick={() => setTarget(null)}
              className="rounded-md border border-border px-3 py-1.5 text-sm font-semibold"
            >
              Abbrechen
            </button>
            <button
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await onUnlink(target.client_id);
                  setTarget(null);
                } finally {
                  setBusy(false);
                }
              }}
              className="inline-flex items-center gap-2 rounded-md bg-brand-button px-3 py-1.5 text-sm font-semibold text-brand-button-foreground disabled:opacity-60"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Verknüpfung lösen
            </button>
          </div>
        </DialogShell>
      )}
    </>
  );
}
